import {
  CurecallModalContainer,
  CurecallButton,
  CurecallSearchableSelect,
  FileTextIcon,
  CrossIcon,
  LoadingSpinner,
  CurecallSelect,
  CurecallSelectItem,
  CurecallSearchableSelectItem,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { PatientService } from 'src/services/patient.service'
import { exportWorkbook } from 'src/utils/xlsx-helper.util'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { TPatientReviewExportRequest } from './reviews.types'

export type Props = {
  projectId: number
  isOpen: boolean
  onClose: VoidFunction
}

type ExportState = 'idle' | 'loading' | 'error'

export const ExportReviewsModal = ({ projectId, isOpen, onClose }: Props) => {
  const { t } = useTranslation()
  const { getMedEventLabelById } = useCurrentProject()

  const currentYear = new Date().getFullYear()

  const YEAR_OPTIONS = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => 2020 + i)
    .reverse()
    .map((year) => ({ label: year.toString(), value: year }))
  const DAYS_OPTIONS = [
    { label: '30 jours', value: 30 },
    { label: '60 jours', value: 60 },
    { label: '90 jours', value: 90 },
  ]

  const [periodType, setPeriodType] = useState<TPatientReviewExportRequest['period']>('specific-year')
  const [selectedOptionYear, setSelectedOptionYear] = useState<CurecallSearchableSelectItem>(YEAR_OPTIONS[0]!)
  const [selectedOptionDays, setSelectedOptionDays] = useState<CurecallSelectItem>(DAYS_OPTIONS[0]!)
  const [exportState, setExportState] = useState<ExportState>('idle')

  const handleClose = () => {
    setExportState('idle')
    setPeriodType('specific-year')
    setSelectedOptionYear(YEAR_OPTIONS[0]!)
    setSelectedOptionDays(DAYS_OPTIONS[0]!)
    onClose()
  }

  const handleExport = async () => {
    setExportState('loading')

    try {
      const reviews = await PatientService.exportPatientReviews(projectId, {
        period: periodType,
        value: periodType === 'specific-year' ? selectedOptionYear.value : selectedOptionDays.value,
      })

      const formattedData = reviews.map((review) => ({
        PatientRemoteId: review.Patient.remoteId,
        PatientReviewCreatedAt: getFormattedDate(review.createdAt),
        PatientReviewNumberValue: review.numberValue,
        PatientReviewComment: review.comment,
        Context1: review.Patient.PatientMedEvents?.[0]?.remoteId ?? '',
        Context2: getMedEventLabelById(review.Patient.PatientMedEvents?.[0]?.MedEventId),
        Context3: review.Patient.PatientMedEvents?.[0]?.metadata?.functionalUnitId
          ? `UF: ${review.Patient.PatientMedEvents[0].metadata.functionalUnitId}`
          : '',
      }))

      const filename =
        periodType === 'specific-year'
          ? `${t('pages.patient-review.title')} - ${selectedOptionYear.value}`
          : `${t('pages.patient-review.title')} - ${selectedOptionDays.value} derniers jours - ${new Date().toLocaleDateString().replaceAll('/', '-')}`

      exportWorkbook({
        dataSheets: { [t('pages.patient-review.sheet-name')]: formattedData },
        filename,
        colHeadersLabel: {
          PatientRemoteId: t('pages.patient-review.remote-id'),
          PatientReviewCreatedAt: t('pages.patient-review.comment-date'),
          PatientReviewNumberValue: t('pages.patient-review.note'),
          PatientReviewComment: t('pages.patient-review.comment'),
          Context1: t('pages.patient-review.context-remote-id'),
          Context2: t('pages.patient-review.context-med-event'),
          Context3: t('pages.patient-review.context-functional-unit'),
        },
        colWidths: {
          [t('pages.patient-review.remote-id')]: 70,
          [t('pages.patient-review.comment-date')]: 100,
          [t('pages.patient-review.note')]: 30,
          [t('pages.patient-review.comment')]: 640,
          [t('pages.patient-review.context-remote-id')]: 80,
          [t('pages.patient-review.context-med-event')]: 180,
          [t('pages.patient-review.context-functional-unit')]: 60,
        },
        rowHeight: 50,
      })

      handleClose()
    } catch (error) {
      console.error('Failed to export patient reviews:', error)
      setExportState('error')
    }
  }

  const handleYearChange = (option: CurecallSearchableSelectItem) => {
    setSelectedOptionYear(option)
  }

  const handleDaysChange = (option: CurecallSelectItem) => {
    setSelectedOptionDays(option)
  }

  return (
    <CurecallModalContainer
      title={t('pages.patient-review.export-modal-title')}
      isOpen={isOpen}
      toggle={handleClose}
    >
      <div className="c-flex c-flex-col c-gap-6">
        <div>
          <p className="c-font-semibold c-mb-2">{t('pages.patient-review.export-modal-subtitle')}</p>
          <p className="c-text-grey-600">{t('pages.patient-review.export-modal-description')}</p>
        </div>

        <div className="c-flex c-gap-4">
          <label className="c-flex c-flex-1 c-items-start c-gap-3 c-cursor-pointer">
            <input
              type="radio"
              name="periodType"
              value="specific-year"
              checked={periodType === 'specific-year'}
              onChange={() => setPeriodType('specific-year')}
              className="c-mt-1"
            />
            <div className="c-flex-1">
              <p className="c-font-semibold c-mb-2">{t('pages.patient-review.year-reviews')}</p>
              <CurecallSearchableSelect
                items={YEAR_OPTIONS}
                onSelect={handleYearChange}
                defaultItemSelected={selectedOptionYear}
                placeholder={t('pages.patient-review.select-year')}
                isDisabled={periodType !== 'specific-year'}
              />
            </div>
          </label>

          <label className="c-flex c-flex-1 c-items-start c-gap-3 c-cursor-pointer">
            <input
              type="radio"
              name="periodType"
              value="recent-days"
              checked={periodType === 'recent-days'}
              onChange={() => setPeriodType('recent-days')}
              className="c-mt-1"
            />
            <div className="c-flex-1">
              <p className="c-font-semibold c-mb-2">{t('pages.patient-review.recent-reviews')}</p>
              <CurecallSelect
                items={DAYS_OPTIONS}
                selectedItem={selectedOptionDays}
                onChange={handleDaysChange}
                placeholder={t('pages.patient-review.select-days')}
                isDisabled={periodType !== 'recent-days'}
              />
            </div>
          </label>
        </div>

        <div className="c-flex c-flex-col c-items-center c-gap-4 c-pt-4 c-border-t c-border-grey-200">
          <div className="c-h-16 c-relative">
            {exportState === 'loading' ? (
              <LoadingSpinner />
            ) : (
              <>
                <FileTextIcon className="c-text-primary" />
                {exportState === 'error' && (
                  <CrossIcon className="c-absolute c-top-0 c-rounded-full c-right-0 c-w-8 c-h-8 c-text-danger-500" />
                )}
              </>
            )}
          </div>

          {exportState === 'error' && (
            <p className="c-text-sm c-text-danger-500 c-font-medium">
              {t('pages.patient-review.export-error')}
            </p>
          )}

          <CurecallButton onClick={handleExport} disabled={exportState === 'loading'} type="button">
            {exportState === 'loading'
              ? t('pages.patient-review.exporting')
              : t('pages.patient-review.export-button')}
          </CurecallButton>
        </div>
      </div>
    </CurecallModalContainer>
  )
}
